import { APP_SETTINGS } from '../config/appSettings'
import type { SearchLayout } from './layoutPreference'

export type BrowseViewportProfile = 'base' | 'phone' | 'wide'

export interface BrowseGeometry {
  cardMaxHeightPx: number
  titleLines: number
  textLines: number
  cardsPerRowMin: number
  cardsPerRowMax: number
  columnGapRem: number
  cardPaddingRem: number
  urlPreviewTextInsetRem: number
}

export interface BrowseModePresentation {
  maxTitleChars: number
  maxTextChars: number
  maxLinkPreviews: number
  geometry: BrowseGeometry
  overrides: Partial<Record<Exclude<BrowseViewportProfile, 'base'>, Partial<BrowseGeometry>>>
}

export interface BrowsePresentation {
  detailedDesktopMinWidthPx: number
  masonryRowUnitPx: number
  layouts: Record<SearchLayout, BrowseModePresentation>
}

export const DEFAULT_BROWSE_PRESENTATION = APP_SETTINGS.browse as BrowsePresentation

export function resolvedBrowseGeometry(
  mode: BrowseModePresentation,
  profile: BrowseViewportProfile,
): BrowseGeometry {
  if (profile === 'base') return mode.geometry
  return { ...mode.geometry, ...mode.overrides[profile] }
}

export function truncateBrowseText(value: string, maxChars: number): string {
  const normalized = value.replace(/\s+/g, ' ').trim()
  if (maxChars <= 0 || normalized.length <= maxChars) return normalized
  return `${normalized.slice(0, Math.max(0, maxChars - 1)).trimEnd()}…`
}

const INLINE_MARKDOWN_LINK_PATTERN = /!?\[[^\]\n]*\]\([^()\s]+(?:\s+["'][^"']*["'])?\)/g

/**
 * Truncates a card's source without leaving a partially-open Markdown link for
 * the preview renderer to expose as literal text. A link which crosses the
 * character boundary is kept whole: its rendered label remains compact even
 * when its destination is long.
 */
export function truncateBrowseMarkdown(value: string, maxChars: number): string {
  if (maxChars <= 0 || value.length <= maxChars) return value

  for (const match of value.matchAll(INLINE_MARKDOWN_LINK_PATTERN)) {
    const start = match.index ?? 0
    const end = start + match[0].length
    if (start < maxChars && end > maxChars) return `${value.slice(0, end)}…`
  }

  return `${value.slice(0, Math.max(0, maxChars - 1)).trimEnd()}…`
}

function cssVariables(geometry: BrowseGeometry): string {
  return [
    `--browse-card-max-height:${geometry.cardMaxHeightPx}px`,
    `--browse-title-lines:${geometry.titleLines}`,
    `--browse-text-lines:${geometry.textLines}`,
    `--browse-cards-per-row-min:${geometry.cardsPerRowMin}`,
    `--browse-cards-per-row-max:${geometry.cardsPerRowMax}`,
    `--browse-column-gap:${geometry.columnGapRem}rem`,
    `--browse-card-padding:${geometry.cardPaddingRem}rem`,
    `--browse-url-preview-text-inset:${geometry.urlPreviewTextInsetRem}rem`,
  ].join(';')
}

/**
 * Converts the typed JSON presentation profiles into CSS-owned responsive rules.
 * No component observes window or visual-viewport changes; the central root form
 * factor and media queries continue to own responsive presentation.
 */
export function browsePresentationCss(presentation: BrowsePresentation): string {
  const rules: string[] = [
    `.search-results{--browse-masonry-row-unit:${presentation.masonryRowUnitPx}px}`,
  ]
  for (const [layout, geometry] of Object.entries(APP_SETTINGS.browse.studioGeometry)) {
    // Studio supplies its base recipe first so the central phone/wide profiles below
    // remain authoritative for responsive column counts and card geometry.
    rules.push(`html[data-theme="minimany"] .search-results--${layout}{--studio-media-max-height:${geometry.mediaMaxHeightPx}px}`)
    rules.push(`html[data-design="studio"] .search-results--${layout}{${cssVariables(geometry)};--studio-card-min-width:${geometry.cardMinWidthPx}px}`)
  }
  for (const layout of Object.keys(presentation.layouts) as SearchLayout[]) {
    const mode = presentation.layouts[layout]
    rules.push(`.search-results--${layout}{${cssVariables(resolvedBrowseGeometry(mode, 'base'))}}`)
    rules.push(`html[data-viewport-form-factor="phone"] .search-results--${layout}{${cssVariables(resolvedBrowseGeometry(mode, 'phone'))}}`)
    rules.push(`@media (min-width:${presentation.detailedDesktopMinWidthPx}px){html[data-viewport-form-factor="desktop"] .search-results--${layout}{${cssVariables(resolvedBrowseGeometry(mode, 'wide'))}}}`)
  }
  rules.push('.search-layout-selector__option--detailed{display:none}')
  rules.push('html[data-viewport-form-factor="phone"] .search-layout-selector__option--detailed{display:grid}')
  rules.push(`@media (min-width:${presentation.detailedDesktopMinWidthPx}px){html[data-viewport-form-factor="desktop"] .search-layout-selector__option--detailed{display:grid}}`)
  return rules.join('\n')
}
